import * as React from "react";
import { Outlet } from "react-router-dom";
import type { RouteObject } from "react-router-dom";

import { smbNavItems } from "@/config/smb";
import { SmbGuard } from "@/components/auth/smb-guard";

// Unlike ./dashboard.tsx, every page here is fetched when its route is first opened: the
// business app exists on self-service deployments only, and an enterprise one never asks for it.

function page<M>(load: () => Promise<M>, pick: (module: M) => React.ComponentType): RouteObject["lazy"] {
	return async () => {
		const module = await load();
		return { Component: pick(module) };
	};
}

// The shell and its navigation land first; each section's real page replaces its
// placeholder as that feature arrives. Driving both off `smbNavItems` keeps them in
// step, so a nav entry can never point at a 404, and landing a section is one line here.
const pages: Record<string, RouteObject["lazy"]> = {
	calendar: page(
		() => import("@/new-pages/smb/calendar"),
		(m) => m.SmbCalendar
	),
	calls: page(
		() => import("@/new-pages/smb/calls"),
		(m) => m.SmbCalls
	),
	customers: page(
		() => import("@/new-pages/smb/customers"),
		(m) => m.SmbCustomers
	),
	outreach: page(
		() => import("@/new-pages/smb/outreach"),
		(m) => m.SmbOutreach
	),
	settings: page(
		() => import("@/new-pages/smb/settings"),
		(m) => m.SmbSettings
	),
};

const sections: RouteObject[] = smbNavItems
	.filter((item) => item.href !== "/")
	.map((item) => {
		const path = item.href.slice(1);
		return {
			path,
			lazy:
				pages[path] ??
				(async () => {
					const { SmbPlaceholderPage } = await import("@/new-pages/smb/components/placeholder-page");
					return {
						element: <SmbPlaceholderPage description="This section is not built yet." title={item.title} />,
					};
				}),
		};
	});

// The root of a self-service deployment (see ./index.tsx).
export const route: RouteObject = {
	lazy: async () => {
		const { SmbLayout } = await import("@/new-pages/smb/layout");
		return {
			element: (
				<SmbGuard>
					<SmbLayout>
						<Outlet />
					</SmbLayout>
				</SmbGuard>
			),
		};
	},
	children: [
		{
			index: true,
			lazy: page(
				() => import("@/new-pages/smb/home"),
				(m) => m.SmbHome
			),
		},
		...sections,
		{
			path: "verification",
			lazy: page(
				() => import("@/new-pages/smb/verification"),
				(m) => m.SmbVerification
			),
		},
		// Not in the navigation: opened from Settings, the Home alert and the Stripe return.
		{
			path: "billing",
			lazy: page(
				() => import("@/new-pages/smb/billing"),
				(m) => m.SmbBilling
			),
		},
		{
			path: "customers/:contactId",
			lazy: page(
				() => import("@/new-pages/smb/customers/customer-profile"),
				(m) => m.SmbCustomerProfile
			),
		},
		{
			path: "outreach/:id",
			lazy: page(
				() => import("@/new-pages/smb/outreach/round"),
				(m) => m.SmbOutreachRound
			),
		},
	],
};

// The setup wizard: SMB accounts only, without the SMB layout chrome.
export const onboardingRoute: RouteObject = {
	path: "onboarding",
	lazy: async () => {
		const { OnboardingWizard } = await import("@/new-pages/smb/onboarding/wizard");
		return {
			element: (
				<SmbGuard allowUnfinishedSetup>
					<OnboardingWizard />
				</SmbGuard>
			),
		};
	},
	children: [
		{
			index: true,
			lazy: page(
				() => import("@/new-pages/smb/onboarding/resume"),
				(m) => m.OnboardingResume
			),
		},
		{
			path: "goal",
			lazy: page(
				() => import("@/new-pages/smb/onboarding/steps/goal"),
				(m) => m.GoalStep
			),
		},
		{
			path: "business-type",
			lazy: page(
				() => import("@/new-pages/smb/onboarding/steps/business-type"),
				(m) => m.BusinessTypeStep
			),
		},
		{
			path: "business-details",
			lazy: page(
				() => import("@/new-pages/smb/onboarding/steps/business-details"),
				(m) => m.BusinessDetailsStep
			),
		},
		{
			path: "services-hours",
			lazy: page(
				() => import("@/new-pages/smb/onboarding/steps/services-hours"),
				(m) => m.ServicesHoursStep
			),
		},
		{
			path: "receptionist",
			lazy: page(
				() => import("@/new-pages/smb/onboarding/steps/receptionist"),
				(m) => m.ReceptionistStep
			),
		},
		{
			path: "phone-number",
			lazy: page(
				() => import("@/new-pages/smb/onboarding/steps/phone-number"),
				(m) => m.PhoneNumberStep
			),
		},
		{
			path: "plan",
			lazy: page(
				() => import("@/new-pages/smb/onboarding/steps/plan"),
				(m) => m.PlanStep
			),
		},
	],
};
